import React from 'react';

interface PullToRefreshIndicatorProps {
  isPulling: boolean;
  pullDistance: number;
  isRefreshing: boolean;
  /** Progress toward threshold (0-1), provided by usePullToRefresh hook */
  progress: number;
}

/**
 * Visual indicator for pull-to-refresh functionality
 * Shows a loading spinner that grows as the user pulls down
 */
export const PullToRefreshIndicator: React.FC<PullToRefreshIndicatorProps> = ({
  isPulling,
  pullDistance,
  isRefreshing,
  progress,
}) => {
  if (!isPulling && !isRefreshing) return null;

  const scale = 0.5 + (progress * 0.5); // Scale from 0.5 to 1
  const opacity = progress;
  const rotation = progress * 360; // Rotate as user pulls

  return (
    <div
      className="fixed top-0 left-0 right-0 flex justify-center items-start pointer-events-none z-50"
      style={{
        transform: `translateY(${isPulling ? pullDistance * 0.5 : 0}px)`,
        transition: isPulling ? 'none' : 'transform 0.3s ease-out',
      }}
    >
      <div
        className="mt-4 p-3 bg-white rounded-full shadow-lg"
        style={{
          transform: `scale(${scale})`,
          opacity,
        }}
      >
        {isRefreshing ? (
          // Spinning loader when refreshing
          <svg
            className="animate-spin h-6 w-6 text-brand-teal"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          // Arrow that rotates as user pulls
          <svg
            className="h-6 w-6 text-brand-teal"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.1s ease-out',
            }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        )}
      </div>

      {/* Text hint when threshold is reached */}
      {isPulling && progress >= 1 && !isRefreshing && (
        <div className="absolute top-16 text-sm text-gray-600 font-medium">
          Release to refresh
        </div>
      )}
    </div>
  );
};
